import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} TextClampRefs
 * @property {HTMLElement} text - The clamped text element
 * @property {HTMLButtonElement} toggleButton - The "read more" / "show less" button
 */

/**
 * A custom element that toggles a CSS line clamp applied on mobile.
 * The button is only shown when the text is actually truncated.
 *
 * @extends {Component<TextClampRefs>}
 */
class TextClampComponent extends Component {
  requiredRefs = ['text', 'toggleButton'];

  /**
   * @type {ResizeObserver | undefined}
   */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.refs.text);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
  }

  get #isExpanded() {
    return this.dataset.expanded === 'true';
  }

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.dataset.expanded = String(expanded);
    this.refs.toggleButton.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * Whether the clamp currently hides part of the text.
   * @returns {boolean}
   */
  #isTruncated() {
    const { text } = this.refs;

    return text.scrollHeight > text.clientHeight + 1;
  }

  /**
   * Syncs the button visibility with the clamp state.
   * The clamp only applies on mobile, so the expanded state is reset on desktop.
   */
  #update = () => {
    if (!isMobileBreakpoint() && this.#isExpanded) this.#setExpanded(false);

    this.refs.toggleButton.hidden = !this.#isExpanded && !this.#isTruncated();
  };

  /**
   * Toggles between the clamped and the full text.
   */
  toggle = () => {
    this.#setExpanded(!this.#isExpanded);
    this.#update();
  };
}

if (!customElements.get('text-clamp-component')) {
  customElements.define('text-clamp-component', TextClampComponent);
}
